import { Controller, useFormContext, useWatch } from "react-hook-form";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { CalendarDays } from "lucide-react";
import { DatePickerV3 } from "../../../../../../components/date-picker-v3";
import { TimePickerV2 } from "../../../../../../components/time-picker-v2";
import { SearchSelect } from "../../../../../../components/search-select-v2";
import type { EventEditorFormValues } from "../event.scheme";
import { renderError, RequiredLabel, SectionTitle, type CommonProps, type Option } from "./shared";

type Props = CommonProps & { timezoneOptions: Option[] };

export function ScheduleSection({ timezoneOptions, isSubmitting, canEditField }: Props) {
  const { control, setValue } = useFormContext<EventEditorFormValues>();
  const start = useWatch({ control, name: "start" });
  const checkIn = useWatch({ control, name: "checkInTime" });
  const { t } = useTranslation();
  const renderTime = (name: "start" | "end" | "checkInTime") => (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <div>
          <TimePickerV2
            value={field.value || ""}
            onChange={(value) => {
              if (!value || !dayjs(value, "HH:mm:ss", true).isValid()) return;
              field.onChange(value);
              if (name === "start" && (!checkIn || checkIn > value))
                setValue("checkInTime", value, { shouldDirty: true, shouldValidate: true });
              if (name === "checkInTime" && start && start < value)
                setValue("start", value, { shouldDirty: true, shouldValidate: true });
            }}
            disabled={isSubmitting || !canEditField(name)}
            className="dark:border-gray-500 dark:bg-gray-800"
          />
          {renderError(fieldState.error?.message)}
        </div>
      )}
    />
  );
  return (
    <section className="event-form-section flex flex-col items-start lg:col-span-2">
      <SectionTitle icon={<CalendarDays className="h-4 w-4" />}>
        {t("event.date")} & {t("event.time")}
      </SectionTitle>
      <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-3">
        <Controller
          control={control}
          name="eventDate"
          render={({ field, fieldState }) => (
            <div>
              <RequiredLabel>{t("event.date")}</RequiredLabel>
              <DatePickerV3
                value={field.value || undefined}
                initialSelectedDate={field.value}
                onChange={(date) => field.onChange(date ?? null)}
                disableToDate={new Date(Date.now() - 86400000)}
                disabled={isSubmitting || !canEditField("eventDate")}
                placeholder="Select date"
                dateFormat="MM/dd/yyyy"
              />
              {renderError(fieldState.error?.message)}
            </div>
          )}
        />
        <div>
          <RequiredLabel>Start</RequiredLabel>
          {renderTime("start")}
        </div>
        <div>
          <RequiredLabel>End</RequiredLabel>
          {renderTime("end")}
        </div>
        <div />
        <Controller
          control={control}
          name="timezone"
          render={({ field, fieldState }) => (
            <div>
              <RequiredLabel>Timezone</RequiredLabel>
              <SearchSelect
                value={field.value || ""}
                onChange={field.onChange}
                options={timezoneOptions}
                placeholder="Select timezone"
                searchPlaceholder="Search timezone..."
                emptyMessage="No timezone found."
                disabled={isSubmitting || !canEditField("timezone")}
                showClear={false}
                side="top"
              />
              {renderError(fieldState.error?.message)}
            </div>
          )}
        />
        <div>
          <RequiredLabel required={false}>{t("event.check_in_time")}</RequiredLabel>
          {renderTime("checkInTime")}
        </div>
      </div>
    </section>
  );
}
